import { Select } from 'bold-ui'
import { useExamesSolicitadosLazyQuery } from 'graphql/hooks.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { partition } from 'lodash'
import React, { useCallback, useMemo } from 'react'

import {
  ANEXO_ARQUIVOS_GRUPOS_PROCEDIMENTO,
  ANEXO_ARQUIVOS_SUBGRUPOS_PROCEDIMENTO,
  ExameSolicitadoSelectModel,
  ResultadoExameAnexoFormModel,
} from '../../model-anexoArquivos'
import { convertResultadosExameToSelectModel } from './converter-examesSolicitados'
import { ExameSolicitadoSelectItem } from './ExameSolicitadoSelectItem'

interface ExameSolicitadoSelectProps {
  onChange: (value: ResultadoExameAnexoFormModel) => void
}

export function ExameSolicitadoSelect(props: Readonly<ExameSolicitadoSelectProps>) {
  const { onChange } = props

  const {
    atendimentoProfissional: {
      lotacao: {
        profissional: { id: profissionalId },
      },
    },
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const [loadExames, { data, loading }] = useExamesSolicitadosLazyQuery({
    variables: {
      input: {
        prontuarioId,
        filtro: {
          apenasExamesSemResultado: true,
          gruposProcedimento: ANEXO_ARQUIVOS_GRUPOS_PROCEDIMENTO,
          subGruposProcedimento: ANEXO_ARQUIVOS_SUBGRUPOS_PROCEDIMENTO,
          procedimentoAtivo: true,
        },
        pageParams: { unpaged: true },
      },
    },
    fetchPolicy: 'network-only',
  })

  const handleOpenSelect = useCallback(() => {
    if (!data) {
      loadExames()
    }
  }, [data, loadExames])

  const exameItems: ExameSolicitadoSelectModel[] = useMemo(
    () => convertResultadosExameToSelectModel(data, profissionalId),
    [data, profissionalId]
  )

  const [meusExames, outrosExames] = useMemo(() => partition(exameItems, (item) => item.idProfissional === null), [
    exameItems,
  ])

  const todosExames = useMemo(() => {
    if (meusExames.length > 0) {
      meusExames[0] = { ...meusExames[0], headerText: 'Solicitados por mim' }
    }
    if (outrosExames.length > 0) {
      outrosExames[0] = { ...outrosExames[0], headerText: 'Solicitado por outros profissionais' }
    }
    return [...meusExames, ...outrosExames]
  }, [meusExames, outrosExames])

  return (
    <Select<ExameSolicitadoSelectModel>
      itemToString={itemToString}
      placeholder='Busque por exames de imagem solicitados'
      items={todosExames}
      components={{
        Item: ExameSolicitadoSelectItem,
      }}
      onFocus={handleOpenSelect}
      loading={loading}
      onChange={(item) => onChange(item as ResultadoExameAnexoFormModel)}
    />
  )
}

const itemToString = (item: ExameSolicitadoSelectModel | null) => (item ? item.descricao : '')
